import { useDispatch } from 'react-redux'
import { AdStorageStatus, type GlobalSettings } from 'common/api/v1/types'
import { ButtonsPane, Form, GridItem, Paper, RHF, SafeRouting, TextInput } from '../common/Form'
import { AppDispatch } from '../../store'
import { saveSettings } from '../../redux/actions/settingsActions'
import { useSettingsSelector } from '../../utils'
import { FormProps } from '../common/Form/RHF'
import Pendable from '../common/Pendable'
import { Api } from '../../store'
import { useEffect, useState } from 'react'
import DataSet from '../common/DataSet'
import { formatFileSize } from 'common/api/v1/helpers'
import LinearProgress from '@mui/material/LinearProgress'
import { useFormContext } from 'react-hook-form'
import { parseK8sMemoryString } from 'common/utils'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
const AdInsertionSettings = ({ formState }: FormProps<GlobalSettings>) => {
  const { saving } = useSettingsSelector()

  const [storageStatus, setStorageStatus] = useState<AdStorageStatus | undefined>(undefined)

  const { getValues, watch } = useFormContext<GlobalSettings>()
  const values = getValues()
  watch('adBucketSizeThreshold')

  const bucketSizeInBytes = parseK8sMemoryString(values.adBucketSizeThreshold || '0')
  const percentUsed = storageStatus?.total.size ? (storageStatus?.total.size / bucketSizeInBytes) * 100 : undefined

  useEffect(() => {
    Api.adStorageApi
      .getAdStorageStatus()
      .then((status) => {
        if (status?.buckets) {
          setStorageStatus(status)
        }
      })
      .catch(() => {
        setStorageStatus(undefined)
      })
  }, [formState])

  return (
    <>
      <SafeRouting formState={formState} />
      <Form id="ad-insertion-form" noValidate>
        <Paper
          title="Storage"
          sx={{
            margin: 0,
            overflow: 'hidden',
            height: '100%',
          }}
        >
          <GridItem lg={6} xl={6}>
            {storageStatus ? <AdStorageStatusDisplay status={storageStatus} /> : undefined}
          </GridItem>
          <GridItem lg={6} xl={6}>
            <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
              <TextInput
                name="adBucketSizeThreshold"
                label="Ad cache size"
                tooltip={`The maximum size of the ad cache. 
                If the size of the ad cache exceeds this value, the retention service will start deleting least recently used ad files. Supports suffixes like G, M, Gi and Mi. 

                NB! If you are using physical storage, this value cannot exceed the combined available disk space of all worker nodes. In cloud environments, more storage can be added dynamically.
                
                Ad Server content does not count towards this limit.
                `}
                required
                validators={{
                  k8sMemoryString: {},
                }}
                sx={{ width: '100%' }}
                fullWidth
              />

              {percentUsed ? (
                <>
                  <LinearProgress
                    value={percentUsed}
                    variant="determinate"
                    sx={{ width: '80%', height: 10 }}
                    color={percentUsed < 70 ? 'success' : percentUsed < 80 ? 'warning' : 'error'}
                  />
                  <Typography variant="body2">Current usage: {percentUsed.toFixed(2)}%</Typography>
                </>
              ) : undefined}
            </Stack>
          </GridItem>
          <ButtonsPane
            main={{
              Save: { primary: true, savingState: saving, type: 'submit' },
            }}
          />
        </Paper>
      </Form>
    </>
  )
}

export const AdInsertionSettingsForm = () => {
  const { settings } = useSettingsSelector()
  const dispatch = useDispatch<AppDispatch>()

  const handleSubmit = async (data: GlobalSettings) => {
    dispatch(saveSettings(data))
  }

  return (
    <Pendable pending={!settings}>
      <RHF
        onSubmit={handleSubmit}
        defaultValues={
          {
            ...settings,
          } as GlobalSettings
        }
        component={AdInsertionSettings}
      />
    </Pendable>
  )
}

const AdStorageStatusDisplay = ({ status }: { status: AdStorageStatus }) => {
  const dataSetValues = {
    'Cached transcoded ads': `${status.buckets.ads.ads.count} files, ${formatFileSize(status.buckets.ads.ads.size)}`,
    'Cached creatives': `${status.buckets.ads.mezzanines.count} files, ${formatFileSize(status.buckets.ads.mezzanines.size)}`,
    'Ad Server Creatives': `${status.buckets.adServer.creatives.count} files, ${formatFileSize(status.buckets.adServer.creatives.size)}`,
    'Ad Server Thumbnails': `${status.buckets.adServer.thumbnails.count} files, ${formatFileSize(status.buckets.adServer.thumbnails.size)}`,
    Total: `${status.total.count} files, ${formatFileSize(status.total.size)}`,
  }

  return <DataSet values={dataSetValues} />
}
